import { useEffect } from 'react';

import { mapColorSchemeStringToMREColorScheme } from '../../helpers/mapColorSchemeStringToMREColorScheme';
import { useMRE } from '../../hooks/useMRE';
import { ColorSchemeString } from '../../types';

interface PersonalHeatmapProps {
  colorScheme: ColorSchemeString;
  // For filter values, MRE performs no validation and inserts strings into URLs
  // as-is. Dates should be provided in `yyyy-MM-dd`. See MRE docs for more info.
  filterStart?: string;
  filterEnd?: string;
  filterType: string;
  includeActivitiesVisibleOnlyToMe: boolean;
  includeActivitiesVisibleToEveryone: boolean;
  includeActivitiesVisibleToFollowersOnly: boolean;
  includeCommutes: boolean;
  respectPrivacyZones: boolean;
}

export const PersonalHeatmap = ({
  colorScheme,
  filterEnd,
  filterStart,
  filterType,
  includeActivitiesVisibleOnlyToMe,
  includeActivitiesVisibleToEveryone,
  includeActivitiesVisibleToFollowersOnly,
  includeCommutes,
  respectPrivacyZones
}: PersonalHeatmapProps) => {
  const { terrainEngine } = useMRE();

  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    terrainEngine.setPersonalHeatmapConfig({
      colorScheme: mapColorSchemeStringToMREColorScheme(colorScheme),
      filterEnd,
      filterStart,
      filterType,
      includeActivitiesVisibleOnlyToMe,
      includeActivitiesVisibleToEveryone,
      includeActivitiesVisibleToFollowersOnly,
      includeCommutes,
      respectPrivacyZones
    });

    // We don't cleanup each time config changes. That would result in unnecessary
    // extra calls to the API.
  }, [
    terrainEngine,
    colorScheme,
    filterEnd,
    filterStart,
    filterType,
    includeActivitiesVisibleOnlyToMe,
    includeActivitiesVisibleToEveryone,
    includeActivitiesVisibleToFollowersOnly,
    includeCommutes,
    respectPrivacyZones
  ]);

  useEffect(() => {
    if (terrainEngine) {
      // We do cleanup when the component unmounts.
      return () => {
        terrainEngine.setPersonalHeatmapConfig(undefined);
      };
    }
  }, [terrainEngine]);

  return null;
};
